import { Link, useParams } from "react-router";
import { ArrowLeft, Lock } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useAuth } from "@/lib/auth-context";
import { useRole } from "@/features/settings/use-role";
import { useRoles } from "@/features/settings/use-roles";
import { useInternalUsers } from "./use-internal-users";
import { EditRoleForm } from "./_components/edit-role-form";
import { DeleteRoleButton } from "./_components/delete-role-button";
import { AssignMembersTab } from "./_components/assign-members-tab";

// Phase 7 sub-bucket 18 — SPA port of /app/admin/settings/roles/[id]/page.tsx.
// Two tabs: Permissions (EditRoleForm) and Members (AssignMembersTab).

export function RoleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const session = useAuth().session;
  const canManage = !!session?.permissions.includes("roles.manage");

  const roleQuery = useRole(id);
  const rolesQuery = useRoles();
  const usersQuery = useInternalUsers();

  if (roleQuery.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const role = roleQuery.data;
  if (!role) {
    return (
      <EmptyState
        icon={Lock}
        title="Role not found"
        description="This role may have been deleted. Return to the list to see what's available."
      />
    );
  }

  const userCount = (usersQuery.data ?? []).filter(
    (u) => u.role_id === role.id,
  ).length;

  return (
    <div>
      <Link
        to="/admin/settings/roles"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to roles
      </Link>

      <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-semibold tracking-tight">
              {role.name}
            </h1>
            {role.is_system && (
              <span className="inline-flex items-center gap-1 rounded-full border bg-muted/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground">
                <Lock className="h-2.5 w-2.5" />
                System
              </span>
            )}
          </div>
          <div className="mt-1.5 flex items-center gap-3 text-xs text-muted-foreground">
            <code className="rounded bg-muted/40 px-1.5 py-0.5 font-mono">
              {role.key}
            </code>
            <span>{userCount} assigned</span>
            {role.description && <span>{role.description}</span>}
          </div>
        </div>
        {!role.is_system && canManage && <DeleteRoleButton id={role.id} />}
      </div>

      {role.is_system && (
        <div className="mb-6 rounded-md border border-accent/30 bg-accent/5 p-3 text-xs text-accent">
          This is a built-in system role. Its name, key and permissions are
          managed by Colab and cannot be changed. You can edit the description
          to add team-specific context.
        </div>
      )}

      <Tabs defaultValue="permissions" className="space-y-4">
        <TabsList>
          <TabsTrigger value="permissions">Permissions</TabsTrigger>
          <TabsTrigger value="members">
            Members
            <span className="ml-1.5 rounded-full bg-muted-foreground/15 px-1.5 py-0.5 text-[10px] tabular-nums text-muted-foreground">
              {userCount}
            </span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="permissions" className="mt-0">
          <EditRoleForm
            roleId={role.id}
            name={role.name}
            description={role.description}
            isSystem={role.is_system}
            initialPermissions={role.permissions}
            canManage={canManage}
          />
        </TabsContent>

        <TabsContent value="members" className="mt-0">
          <AssignMembersTab
            roles={rolesQuery.data ?? []}
            canManage={canManage}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
